/**
 * SuperballBingoStyleLiveBoard sections — 75-number bingo board as hero centerpiece.
 * Theme-var driven with B·I·N·G·O columns, call glow highlighting.
 */
import React, { useMemo } from 'react'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import DrawCountdownHeader from '../../../components/DrawCountdownHeader'
import { useContent } from '../../../contexts/ContentContext'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'
import { formatCurrencyValue } from '../../../utils/formatCurrency'
import { useTranslations } from '../../../contexts/TranslationContext'

const BINGO_COLUMNS = [
  { letter: 'B', range: [1, 15] },
  { letter: 'I', range: [16, 30] },
  { letter: 'N', range: [31, 45] },
  { letter: 'G', range: [46, 60] },
  { letter: 'O', range: [61, 75] },
]

function getLetterForNumber(n) {
  if (n >= 1 && n <= 15) return 'B'
  if (n <= 30) return 'I'
  if (n <= 45) return 'N'
  if (n <= 60) return 'G'
  if (n <= 75) return 'O'
  return '?'
}

/**
 * Board cell — theme-var driven colors with glow effect for called numbers.
 */
const BoardCell = ({ number, isCalled, isLatest }) => (
  <div
    className={`aspect-square rounded-md sm:rounded-lg flex items-center justify-center text-[10px] sm:text-xs md:text-sm font-black transition-all border ${
      isLatest ? 'ring-2 ring-white scale-110 z-10' : ''
    }`}
    style={
      isCalled
        ? {
            backgroundColor: 'var(--charity-primary)',
            color: 'var(--charity-primary-contrast)',
            borderColor: 'color-mix(in oklab, var(--charity-primary-contrast) 40%, transparent)',
            boxShadow: isLatest
              ? '0 0 18px 2px var(--charity-primary)'
              : '0 0 10px 0 color-mix(in oklab, var(--charity-primary) 50%, transparent)',
          }
        : {
            backgroundColor: 'color-mix(in oklab, var(--charity-primary-contrast) 15%, transparent)',
            color: 'color-mix(in oklab, var(--charity-primary-contrast) 70%, transparent)',
            borderColor: 'color-mix(in oklab, var(--charity-primary-contrast) 20%, transparent)',
          }
    }
  >
    {number}
  </div>
)

/**
 * Hero section — 75-number BINGO board as centerpiece with latest call ring + jackpot/countdown sidebar.
 */
export function BingoLiveBoardHeroSection({
  mainDraw,
  calledBalls = [],
  sessionData,
  editMode,
  handleTranslationUpdate,
  timeLeft,
  purchaseNavigate, language,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}

  const drawnBalls = Array.isArray(calledBalls) ? calledBalls : []
  const superballDrawn = drawnBalls.includes(76)
  const calledNumbers = drawnBalls.filter((n) => n >= 1 && n <= 75)
  const calledSet = useMemo(() => new Set(calledNumbers), [calledNumbers])
  const latestCall = calledNumbers.length > 0 ? calledNumbers[calledNumbers.length - 1] : null
  const ballsRemaining = 76 - drawnBalls.length

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 5000)
  const sessionPrize = mainDraw?.session_prize ?? 120

  const heroGradientStyle = {
    backgroundImage:
      'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
    color: 'var(--charity-primary-contrast)',
  }

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const logoUrl = isDefaultLogo ? brandLogo : branding?.logoUrl || brandLogo
  const logoAlt = branding?.charityName || brandName || tr('common.charityPartner', 'Charity Partner')

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={heroGradientStyle}>
      <div className="absolute inset-0 bg-black/15 pointer-events-none" aria-hidden="true" />

      {/* Glow orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <HeroSiteLogo tone="dark" />
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          {/* LEFT — logo + title + latest-call + CTA */}
          <div className="lg:col-span-4 text-center flex flex-col items-center">
            {logoUrl && (
              <div className="mb-5 flex justify-center">
                <div className="relative group">
                  <div
                    className="absolute -inset-3 rounded-2xl blur-xl opacity-40"
                    style={{ backgroundColor: 'var(--charity-primary-contrast)' }}
                    aria-hidden="true"
                  />
                  <div className="relative bg-white rounded-2xl px-6 py-4 shadow-2xl">
                    <img
                      src={logoUrl}
                      alt={logoAlt}
                      className="h-14 sm:h-16 lg:h-20 w-auto object-contain max-w-[240px]"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-bold mb-4">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
              {mainDraw?.name || (
                <InlineTranslationEdit
                  translationKey="liveBoard.liveLabel"
                  value={c('liveBoard.liveLabel', 'Superball Bingo — LIVE')}
                  fallback="Superball Bingo — LIVE"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              )}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 leading-tight">
              <InlineTranslationEdit
                translationKey="liveBoard.heroTitle"
                value={c('liveBoard.heroTitle', 'Live Board')}
                fallback="Live Board"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h1>

            <HeroPartnerGlassSelect className="mx-auto mb-5 w-full max-w-sm" />

            {/* Ball drop — latest call */}
            <div className="w-full max-w-sm mb-5">
              <p className="text-xs uppercase tracking-wider text-white/80 font-semibold mb-2">
                <InlineTranslationEdit
                  translationKey="liveBoard.latestCall"
                  value={c('liveBoard.latestCall', 'Latest Call')}
                  fallback="Latest Call"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
              {latestCall != null ? (
                <div
                  className="mx-auto w-28 h-28 sm:w-32 sm:h-32 rounded-full flex flex-col items-center justify-center shadow-2xl border-4 border-white/60 animate-pulse"
                  style={{
                    backgroundColor: 'var(--charity-primary)',
                    color: 'var(--charity-primary-contrast)',
                  }}
                  aria-label={tr('liveBoard.latestCallValue', 'Latest call {value}')
                    .replace('{value}', `${getLetterForNumber(latestCall)}-${latestCall}`)}
                >
                  <span className="text-xs font-black uppercase tracking-wider opacity-80">
                    {getLetterForNumber(latestCall)}
                  </span>
                  <span className="text-4xl sm:text-5xl font-black leading-none">{latestCall}</span>
                </div>
              ) : (
                <div
                  className="mx-auto w-28 h-28 sm:w-32 sm:h-32 rounded-full flex items-center justify-center shadow-xl border-4 border-white/30"
                  style={{
                    backgroundColor: 'color-mix(in oklab, var(--charity-primary-contrast) 15%, transparent)',
                    color: 'white',
                  }}
                >
                  <Sparkles className="w-10 h-10 opacity-70" />
                </div>
              )}
              <p className="text-xs text-white/70 mt-2">
                {tr('liveBoard.callsSummary', '{called} called · {remaining} balls remaining')
                  .replace('{called}', calledNumbers.length)
                  .replace('{remaining}', ballsRemaining)}
              </p>
            </div>

            <button
              onClick={() => purchaseNavigate('/purchase')}
              className="group relative inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 bg-white text-lg font-black rounded-2xl transition-all hover:scale-105 hover:shadow-2xl shadow-xl"
              style={{ color: 'var(--charity-primary)' }}
            >
              <Ticket className="w-5 h-5" />
              <InlineTranslationEdit
                translationKey="liveBoard.getTickets"
                value={c('liveBoard.getTickets', 'Get Your Card')}
                fallback="Get Your Card"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* CENTER — 75-number board */}
          <div className="lg:col-span-5">
            <div
              className="relative rounded-3xl p-4 sm:p-6 shadow-2xl border border-white/30"
              style={{
                backgroundColor: 'color-mix(in oklab, var(--charity-primary-contrast) 8%, rgba(0,0,0,0.35))',
                backdropFilter: 'blur(8px)',
              }}
            >
              <div className="grid grid-cols-5 gap-1.5 sm:gap-2 mb-2">
                {BINGO_COLUMNS.map((col) => (
                  <div
                    key={col.letter}
                    className="aspect-square rounded-md flex items-center justify-center text-2xl sm:text-3xl font-black text-white"
                  >
                    {col.letter}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                {BINGO_COLUMNS.map((col) => (
                  <div key={col.letter} className="flex flex-col gap-1.5 sm:gap-2">
                    {Array.from({ length: col.range[1] - col.range[0] + 1 }, (_, i) => {
                      const num = col.range[0] + i
                      return (
                        <BoardCell
                          key={num}
                          number={num}
                          isCalled={calledSet.has(num)}
                          isLatest={num === latestCall}
                        />
                      )
                    })}
                  </div>
                ))}
              </div>

              {/* Superball footer */}
              <div className="mt-4 pt-3 border-t border-white/20 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Star className="w-5 h-5" fill="currentColor" />
                  <span className="text-sm font-black uppercase tracking-wider">Superball (76)</span>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-black uppercase ${
                    superballDrawn ? '' : 'opacity-70'
                  }`}
                  style={{
                    backgroundColor: superballDrawn
                      ? 'var(--charity-primary)'
                      : 'color-mix(in oklab, var(--charity-primary-contrast) 20%, transparent)',
                    color: 'white',
                  }}
                >
                  {superballDrawn
                    ? tr('liveBoard.jackpotDrawn', 'DRAWN — JACKPOT!')
                    : tr('liveBoard.awaitingDraw', 'Awaiting draw')}
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT — jackpot + countdown */}
          <div className="lg:col-span-3 flex flex-col items-center lg:items-stretch gap-4">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl blur-xl opacity-40 bg-white" aria-hidden="true" />
              <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-5 shadow-2xl border border-white/30">
                <p className="text-white/80 font-bold uppercase text-xs tracking-wider mb-1 text-center">
                  <InlineTranslationEdit
                    translationKey="liveBoard.jackpotLabel"
                    value={c('liveBoard.jackpotLabel', 'Superball Jackpot')}
                    fallback="Superball Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <p className="text-4xl md:text-5xl font-black text-center mb-3 text-white">
                  {formatCurrencyValue(jackpotAmount, { language })}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    className="rounded-lg p-2 text-center border border-white/30"
                    style={{ backgroundColor: 'color-mix(in oklab, var(--charity-primary) 22%, transparent)' }}
                  >
                    <p className="text-[10px] text-white/80 uppercase">{tr('superballBingo.sessionPrize', 'Session Prize')}</p>
                    <p className="text-base font-black text-white">{formatCurrencyValue(sessionPrize, { language })}</p>
                  </div>
                  <div
                    className="rounded-lg p-2 text-center border border-white/30"
                    style={{
                      backgroundColor:
                        'color-mix(in oklab, var(--charity-secondary, var(--charity-primary)) 22%, transparent)',
                    }}
                  >
                    <p className="text-[10px] text-white/80 uppercase">{tr('superballBingo.ballsRemaining', 'Balls Remaining')}</p>
                    <p className="text-base font-black text-white">{ballsRemaining}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/30">
              <DrawCountdownHeader draw={mainDraw} className="mb-2" labelClassName="text-white/80">
                <InlineTranslationEdit
                  translationKey="liveBoard.drawIn"
                  value={c('liveBoard.drawIn', 'Draw In')}
                  fallback="Draw In"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </DrawCountdownHeader>
              <div className="flex gap-2 justify-center">
                {[
                  { val: timeLeft?.days || 0, label: tr('countdown.days', 'Days').slice(0, 1) },
                  { val: timeLeft?.hours || 0, label: tr('countdown.hours', 'Hours').slice(0, 1) },
                  { val: timeLeft?.minutes || 0, label: tr('countdown.minutes', 'Minutes').slice(0, 1) },
                  { val: timeLeft?.seconds || 0, label: tr('countdown.seconds', 'Seconds').slice(0, 1) },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 bg-white/25 backdrop-blur-sm rounded-lg flex items-center justify-center text-lg font-black text-white shadow border border-white/30">
                      {String(item.val).padStart(2, '0')}
                    </div>
                    <span className="text-[9px] text-white/70 mt-0.5 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Jackpot summary strip — jackpot + called count + CTA.
 */
export function BingoLiveBoardJackpotSummarySection({
  mainDraw,
  calledBalls = [],
  purchaseNavigate, language,
}) {
  const { t: tr } = useTranslations()
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 5000)
  const calledNumbers = (Array.isArray(calledBalls) ? calledBalls : []).filter((n) => n >= 1 && n <= 75)

  return (
    <section className="relative z-20 py-6">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-left">
              <p
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: 'var(--charity-primary)' }}
              >
                {tr('recentWinners.jackpot', 'Jackpot')}
              </p>
              <p className="text-3xl md:text-4xl font-black text-gray-900 dark:text-white">
                {formatCurrencyValue(jackpotAmount, { language })}
              </p>
            </div>
            <div className="text-center border-l border-r border-gray-200 dark:border-gray-700 px-6">
              <p
                className="text-sm font-semibold uppercase tracking-wider"
                style={{ color: 'var(--charity-secondary, var(--charity-primary))' }}
              >
                {tr('liveBoard.called', 'Called')}
              </p>
              <p className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
                {calledNumbers.length} / 75
              </p>
            </div>
            <div className="text-center md:text-right">
              <button
                onClick={() => purchaseNavigate('/purchase')}
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl hover:scale-105 transition-all shadow-lg"
                style={{
                  backgroundImage:
                    'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
                  color: 'var(--charity-primary-contrast)',
                }}
              >
                <Ticket className="w-5 h-5" />
                {tr('superballBingo.getTickets', 'Get Your Card')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Stats + recent winners section.
 */
export function BingoLiveBoardStatsWinnersSection({
  mainDraw,
  calledBalls = [],
  editMode,
  handleTranslationUpdate, language,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 5000)
  const sessionPrize = mainDraw?.session_prize ?? 120
  const ticketsSold = mainDraw?.total_tickets_sold
  const calledNumbers = (Array.isArray(calledBalls) ? calledBalls : []).filter((n) => n >= 1 && n <= 75)

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: 'var(--charity-primary)' }} />
              {tr('liveBoard.sessionImpact', "This Session's Impact")}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-900">
                <DollarSign className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--charity-primary)' }} />
                <p className="text-2xl font-black text-gray-900 dark:text-white">
                  {formatCurrencyValue(jackpotAmount, { language })}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr('recentWinners.jackpot', 'Jackpot')}</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-900">
                <Trophy
                  className="w-8 h-8 mx-auto mb-2"
                  style={{ color: 'var(--charity-secondary, var(--charity-primary))' }}
                />
                <p className="text-2xl font-black text-gray-900 dark:text-white">
                  {formatCurrencyValue(sessionPrize, { language })}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr('superballBingo.sessionPrize', 'Session Prize')}</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-900">
                <Star
                  className="w-8 h-8 mx-auto mb-2"
                  style={{ color: 'var(--charity-accent, var(--charity-primary))' }}
                />
                <p className="text-2xl font-black text-gray-900 dark:text-white">{calledNumbers.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{tr('liveBoard.numbersCalled', 'Numbers Called')}</p>
              </div>
              {ticketsSold != null && (
                <div className="text-center p-4 rounded-xl bg-gray-100 dark:bg-gray-900">
                  <Users className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {ticketsSold.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{tr('liveBoard.cardsSold', 'Cards Sold')}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5" style={{ color: 'var(--charity-primary)' }} />
              {tr('recentWinners.title', 'Recent Winners')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{tr('liveBoard.winnersPlaceholder', 'Winners will appear here after the draw.')}</p>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * How-it-works section — bingo instructions.
 */
export function BingoLiveBoardHowItWorksSection({ editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const steps = [
    { title: 'Buy Tickets', desc: 'Purchase your bingo tickets', titleKey: 'liveBoardStep1Title', descKey: 'liveBoardStep1Desc' },
    { title: 'Pick Your Ball', desc: 'Choose your lucky ball', titleKey: 'liveBoardStep2Title', descKey: 'liveBoardStep2Desc' },
    { title: 'Watch the Draw', desc: 'Watch the live ball draw', titleKey: 'liveBoardStep3Title', descKey: 'liveBoardStep3Desc' },
    { title: 'Win Prizes', desc: 'Match the superball!', titleKey: 'liveBoardStep4Title', descKey: 'liveBoardStep4Desc' },
  ]

  return (
    <section className="py-12 bg-white dark:bg-gray-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
          <InlineTranslationEdit
            translationKey="liveBoard.howItWorks"
            value={c('liveBoard.howItWorks', 'How to Play')}
            fallback="How to Play"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {steps.map((step, i) => (
            <div
              key={i}
              className="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700"
            >
              <div className="text-3xl font-black mb-2" style={{ color: 'var(--charity-primary)' }}>
                {i + 1}
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                <InlineTranslationEdit translationKey={step.titleKey} value={c(step.titleKey, step.title)} fallback={step.title} onUpdate={handleTranslationUpdate} editMode={editMode} />
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <InlineTranslationEdit translationKey={step.descKey} value={c(step.descKey, step.desc)} fallback={step.desc} onUpdate={handleTranslationUpdate} editMode={editMode} />
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * Final CTA section.
 */
export function BingoLiveBoardFinalCTASection({ editMode, handleTranslationUpdate, purchaseNavigate }) {
  const { c } = useContent()

  return (
    <section className="py-12 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <Gift className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--charity-primary)' }} />
        <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
          <InlineTranslationEdit
            translationKey="liveBoard.finalCTATitle"
            value={c('liveBoard.finalCTATitle', 'Ready to Win?')}
            fallback="Ready to Win?"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
          <InlineTranslationEdit
            translationKey="liveBoard.finalCTADesc"
            value={c('liveBoard.finalCTADesc', 'Get your bingo card now and try your luck!')}
            fallback="Get your bingo card now and try your luck!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
        <button
          onClick={() => purchaseNavigate('/purchase')}
          className="inline-flex items-center gap-2 px-10 py-5 text-white text-xl font-black rounded-2xl hover:scale-105 transition-all shadow-lg"
          style={{
            backgroundImage:
              'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
            color: 'var(--charity-primary-contrast)',
          }}
        >
          <Ticket className="w-6 h-6" />
          <InlineTranslationEdit
            translationKey="liveBoard.getYourCard"
            value={c('liveBoard.getYourCard', 'Get Your Card')}
            fallback="Get Your Card"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
          <ArrowRight className="w-6 h-6" />
        </button>
      </div>
    </section>
  )
}
